// TODO: Safari does not support webp. Could we figure out whether we need transparency, and use jpeg when we don't?
Px.CanvasPageRenderer = class CanvasPageRenderer {

  constructor(options) {
    this.fulfillment = Boolean(options.fulfillment);
    this.minimum_dpi = options.minimum_dpi;

    this.hide_placeholders = options.hide_placeholders === true;
    this.hide_resolution_warnings = options.hide_resolution_warnings === true;
    this.hide_scorelines = options.hide_scorelines === false ? false : true;

    this.transparent_background_color = options.transparent_background_color;
    this.disable_text_removal = options.disable_text_removal === true;

    this.oncomplete = options.oncomplete;
    this.onprogress = options.onprogress;

    if (options.ctx) {
      this.ctx = options.ctx;
      this.canvas = this.ctx.canvas;
    } else {
      this.canvas = document.createElement('canvas');
      this.ctx = this.canvas.getContext('2d');
    }

    this.layer_definitions = options.layer_definitions;
    this.layers = options.layers;
    this.image_mask_mode = options.image_mask_mode;

    this.images = {};
    this.pdfs = {};
    this.render_stack = [];
    this.async_resources = {};

  }

  render(print_page, options) {
    options = options || {};

    print_page.images.forEach(image => {
      this.images[image.id] = image;
    });

    print_page.pdfs.forEach(pdf => {
      this.pdfs[pdf.id] = pdf;
    });

    if (this.render_stack.some(frame => frame.print_page.id === print_page.id)) {
      const main_page = this.render_stack[0].print_page;
      throw new Error(`Failed rendering page ${main_page.id}; circular reference detected: inline page ${print_page.id}.`);
    }

    this.render_frame = this.buildRenderFrame(print_page, options);
    this.render_stack.push(this.render_frame);

    this.render_sequence_number = 0;

    const frame = this.render_frame;
    this.canvas.width = frame.cropped_pixel_width;
    this.canvas.height = frame.cropped_pixel_height;

    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';

    this.ctx.scale(frame.scale, frame.scale);
    this.ctx.translate(-1 * frame.view_box[0], -1 * frame.view_box[1]);

    this.renderPage();
  }

  // -------
  // Private
  // -------

  rerender() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.renderPage();
  }

  renderPage() {
    this.render_sequence_number++;
    this.are_resources_pending = false;

    const page_node = this.render_frame.page_node;

    // Render background color.
    const bgcolor = this.parseColorAttr(page_node, 'bgcolor', '#FFFFFF');
    if (!this.transparent_background_color ||
        (this.transparent_background_color.toUpperCase() !== bgcolor.toUpperCase())) {
      if (this.isRenderingBaseLayer() && !(this.image_mask_mode || this.image_overlay_mode)) {
        this.ctx.fillStyle = bgcolor;
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      }
    }

    // Render background image.
    if (this.isRenderingBaseLayer() && !(this.image_mask_mode || this.image_overlay_mode)) {
      const bg_image_src = page_node.getAttribute('src');
      if (bg_image_src) {
        const bg_image = this.getImageFromSrc(bg_image_src);
        if (bg_image) {
          const {page_width, page_height, page_pixel_width, page_pixel_height} = this.render_frame;
          const max_size = Math.max(page_pixel_width, page_pixel_height);
          // TODO: Add support for `srccolor` attribute.
          const bg_image_url = this.imageUrl(bg_image, max_size);
          const img = this.fetchImage(bg_image_url);
          if (img) {
            if (img.success) {
              const bg_image_ar = img.data.width / img.data.height;
              let dims;
              let dx = 0;
              let dy = 0;
              if (page_node.getAttribute('bgfill') === 'true') {
                dims = {width: page_width, height: page_height};
              } else {
                dims = Px.Util.inscribedRectangleDimensions(page_width, page_height, 0, bg_image_ar);
                dx = (page_width - dims.width) / 2;
                dy = (page_height - dims.height) / 2;
              }
              this.ctx.drawImage(img.data, dx, dy, dims.width, dims.height);
            }
          }
        }
      }
    }

    // Render page content.
    this.applyElementTransform(page_node, () => {
      this.renderGroup(page_node);
    });

    // Clip the page with the mask.
    const mask_image_src = page_node.getAttribute('mask');
    if (mask_image_src) {
      const mask_image = this.getImageFromSrc(mask_image_src);
      if (mask_image) {
        const {page_width, page_height, page_pixel_width, page_pixel_height} = this.render_frame;
        const max_size = Math.max(page_pixel_width, page_pixel_height);
        const mask_image_url = this.imageUrl(mask_image, max_size);
        const img = this.fetchImage(mask_image_url);
        if (img) {
          if (img.success) {
            const mask_image_ar = img.data.width / img.data.height;
            const dims = Px.Util.inscribedRectangleDimensions(page_width, page_height, 0, mask_image_ar);
            const dx = (page_width - dims.width) / 2;
            const dy = (page_height - dims.height) / 2;
            this.saveGraphicsState(() => {
              this.ctx.globalCompositeOperation = 'destination-in';
              this.ctx.drawImage(img.data, dx, dy, dims.width, dims.height);
            });
          }
        } else {
          // If we have a mask image, but it's not loaded yet, don't invoke progress callbacks
          // to avoid flickering (it looks bad when page is first visible in its entirety, but then gets clipped
          // once the mask finishes loading).
          return;
        }
      }
    }

    if (this.are_resources_pending) {
      this.emitProgress();
    } else {
      this.emitComplete();
    }

  }

  renderElement(element, renderCtx) {
    const pdf_layer = element.getAttribute('pdf_layer');
    if (!(this.shouldRenderPdfLayer(pdf_layer) && this.isPdfLayerVisible(pdf_layer))) {
      return;
    }

    const type = element.tagName.toLowerCase();

    if (this.image_mask_mode) {
      if (!(type === 'g' || (type === 'image' && element.getAttribute('name') === this.image_mask_mode))) {
        return;
      }
    }

    if (this.image_overlay_mode && !renderCtx.overlay_target_found) {
      if (type === 'image' && element.getAttribute('name') === this.image_overlay_mode) {
        renderCtx.overlay_target_found = true;
      } else {
        return;
      }
    }

    this.applyElementTransform(element, () => {
      if (type === 'g') {
        this.renderGroup(element, renderCtx);
      } else if (type === 'image') {
        this.renderImage(element, renderCtx);
      } else if (type === 'text') {
        this.renderText(element);
      } else if (type === 'score') {
        this.renderScoreline(element);
      } else if (type === 'grid') {
        this.renderGrid(element);
      } else if (type === 'qrcode') {
        this.renderQrCode(element);
      } else if (type === 'barcode') {
        this.renderBarcode(element);
      } else if (type === 'pdf') {
        this.renderPdf(element);
      } else if (type === 'ipage') {
        this.renderIPage(element);
      }
    });
  }

  renderGroup(group, renderCtx) {
    renderCtx = renderCtx || {overlay_target_found: false};

    this.getNodeChildren(group).forEach(element => {
      this.renderElement(element, renderCtx);
    });
  }

  renderIPage(element) {
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);
    const border_radius = this.parseFloatAttr(element, 'radius');

    const shadow_opacity = this.parseFloatAttr(element, 'shadow_opacity');
    const shadow_stdev = this.parseFloatAttr(element, 'shadow_stdev');
    const shadow_ox = this.parseFloatAttr(element, 'shadow_ox');
    const shadow_oy = this.parseFloatAttr(element, 'shadow_oy');
    const shadow_color = this.parseColorAttr(element, 'shadow_color');

    let opacity_hex = Math.round(shadow_opacity * 255).toString(16);
    if (opacity_hex.length === 1) {
      opacity_hex = '0' + opacity_hex;
    }
    this.ctx.shadowColor = shadow_color + opacity_hex;
    // We multiply shadow_stdev by 8 to get an effect similar to the prawn and SVG renderers.
    // The value was determined experimentally.
    this.ctx.shadowBlur = 8 * shadow_stdev;
    this.ctx.shadowOffsetX = shadow_ox;
    this.ctx.shadowOffsetY = shadow_oy;

    this.saveGraphicsState(() => {
      this.ctx.beginPath();
      if (this.ctx.roundRect) {
        this.ctx.roundRect(0, 0, width, height, border_radius);
      } else {
        // TODO: Fallback for browsers that don't support `roundRect()`.
      }
      this.ctx.fill();
    });

    // Reset the shadow.
    this.ctx.shadowColor = 'transparent';
  }

  renderImage(element, renderCtx) {
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);

    if (width <= 0 || height <= 0) {
      return;
    }

    let border_width = this.parseFloatAttr(element, 'border');
    const border_radius = this.parseFloatAttr(element, 'radius');
    const border_color = this.parseColorAttr(element, 'bordercolor');
    const borderwrap = this.parseFloatAttr(element, 'borderwrap');
    const crotation = this.parseFloatAttr(element, 'crotation');
    const left_offset_ratio = this.parseFloatAttr(element, 'left');
    const top_offset_ratio = this.parseFloatAttr(element, 'top');

    let image = null;
    let is_missing_image = false;
    let is_lowres = false;
    let img_ratio = 1;

    if (element.hasAttribute('src') && !this.removeBecausePlaceholder(element)) {
      image = this.getImageFromElement(element);
      if (image) {
        is_missing_image = false;
        img_ratio = image.width / image.height;
        is_lowres = this.resolutionTooLow(image.width, image.height, element);
      } else {
        is_missing_image = true;
        is_lowres = false;
        img_ratio = 1;
      }
    } else {
      is_missing_image = false;
      is_lowres = false;
      img_ratio = 1;
    }

    const img_w = width - 2 * borderwrap;
    const img_h = height - 2 * borderwrap;
    const positioned_image_dimensions = this.positionedImageDimensions(img_w, img_h, element, img_ratio);
    const positioned_width = positioned_image_dimensions[0];
    const positioned_height = positioned_image_dimensions[1];
    const max_size = Math.max(positioned_width, positioned_height) * this.render_frame.scale;

    let src;

    if (this.image_mask_mode && this.image_mask_mode === element.getAttribute('name')) {
      src = CanvasPageRenderer.PLACEHOLDER_10x10_WHITE;
      border_width = 0;
    } else if (this.image_overlay_mode && this.image_overlay_mode === element.getAttribute('name')) {
      src = CanvasPageRenderer.PLACEHOLDER_10x10_TRANSPARENT;
    } else if (image) {
      src = this.imageUrlWithEffects(image, max_size, element);
    } else if (is_missing_image) {
      if (element.getAttribute('src').startsWith('del:')) {
        src = CanvasPageRenderer.DELETED_IMAGE_SRC;
      } else {
        src = CanvasPageRenderer.MISSING_IMAGE_SRC;
      }
    } else {
      src = CanvasPageRenderer.PLACEHOLDER_10x10_GRAY;
    }

    // Fetch the mask image if image is masked.
    const mask_image_src = element.getAttribute('mask');
    let mask_img;
    if (mask_image_src) {
      const mask_image = this.getImageFromSrc(mask_image_src);
      if (mask_image) {
        const scale = this.render_frame.scale;
        const max_size = Math.max(Math.round(width * scale), Math.round(height * scale));
        const mask_image_url = this.imageUrl(mask_image, max_size);
        mask_img = this.fetchImage(mask_image_url);
        if (mask_img) {
          if (!mask_img.success) {
            // If mask fails to load for whatever reason, we don't render the image at all.
            return;
          }
        } else {
          // If the image is masked, but the mask image is not loaded yet, don't render the image to avoid flickering
          // (it looks bad when image is first entirely visible, but then gets clipped once the mask finishes loading).
          return;
        }
      }
    }

    const img = this.fetchImage(src);
    if (img && img.success) {
      this.saveGraphicsState(() => {
        this.ctx.beginPath();
        if (this.ctx.roundRect) {
          this.ctx.roundRect(0, 0, width, height, border_radius);
        } else {
          // TODO: Fallback for browsers that don't support `roundRect()`.
        }
        this.ctx.clip();

        // If the image is masked we have to compose it on a "buffer" canvas first before drawing to the main canvas.
        const ctx = mask_img ? this.getBufferCanvasContext(width, height) : this.ctx;

        if (mask_img) {
          ctx.drawImage(mask_img.data, 0, 0, width, height);
          // TODO: In order for borderwrap to render correctly with transparent images,
          //       I think we'll have to render the borderwrapped image first, and then
          //       impose the maks over it with 'destination-in'.
          ctx.globalCompositeOperation = 'source-atop';
        }

        let borderwrap_positions = ['center'];
        if (borderwrap > 0) {
          borderwrap_positions = [
            'center',
            'top-left',
            'top',
            'top-right',
            'right',
            'bottom-right',
            'bottom',
            'bottom-left',
            'left'
          ];
        }

        const dx = (positioned_width - width) / 2 - (positioned_width * left_offset_ratio/100);
        const dy = (positioned_height - height) / 2 - (positioned_height * top_offset_ratio/100);

        borderwrap_positions.forEach(pos => {
          this.saveGraphicsState(() => {
            this.borderwrapTransform(ctx, pos, width, height, borderwrap);
            ctx.translate(width/2, height/2);
            ctx.rotate(Math.PI * crotation / 180);
            ctx.translate(-width/2, -height/2);
            ctx.translate(-dx, -dy);
            ctx.drawImage(img.data, 0, 0, positioned_width, positioned_height);
          });
        });

        if (mask_img) {
          this.ctx.drawImage(this.buffer_canvas, 0, 0, width, height);
        }
      });
    }
    // TODO: Handle image.success === false.

    if (border_width) {
      this.ctx.strokeStyle = border_color;
      this.ctx.lineWidth = border_width;
      if (this.ctx.roundRect) {
        this.ctx.roundRect(0, 0, width, height, border_radius);
      } else {
        // TODO: Fallback for browsers that don't support `roundRect()`.
      }
      this.ctx.stroke();
    }

    // TODO: Resolution warnings.
  }

  borderwrapTransform(ctx, position, width, height, borderwrap) {
    // We nudge the borderwrap images half a pixel towards the center to avoid ugly looking
    // gaps that can occur because of rounding.
    let epsilon_x, epsilon_y;
    if (position !== 'center') {
      const matrix = ctx.getTransform();
      const scale_x = Math.sqrt(matrix.a ** 2 + matrix.b ** 2);
      const scale_y = Math.sqrt(matrix.c ** 2 + matrix.d ** 2);
      epsilon_x = 0.5 * scale_x;
      epsilon_y = 0.5 * scale_y;
    }

    switch (position) {
    case 'top-left':
      ctx.translate(epsilon_x, epsilon_y);
      ctx.beginPath();
      ctx.rect(0, 0, borderwrap + 2 * epsilon_x, borderwrap + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(2 * borderwrap, 2 * borderwrap);
      ctx.scale(-1, -1);
      break;
    case 'top':
      ctx.translate(0, epsilon_y);
      ctx.beginPath();
      ctx.rect(borderwrap , 0, width - 2 * borderwrap + 2 * epsilon_x, borderwrap + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(0, 2 * borderwrap);
      ctx.scale(1, -1);
      break;
    case 'top-right':
      ctx.translate(-epsilon_x, epsilon_y);
      ctx.beginPath();
      ctx.rect(width - borderwrap, 0, borderwrap + 2 * epsilon_x, borderwrap + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(2 * (width - borderwrap), 2 * borderwrap);
      ctx.scale(-1, -1);
      break;
    case 'right':
      ctx.translate(-epsilon_x, 0);
      ctx.beginPath();
      ctx.rect(width - borderwrap, borderwrap, borderwrap + 2 * epsilon_x, height - 2 * borderwrap + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(2 * (width - borderwrap), 0);
      ctx.scale(-1, 1);
      break;
    case 'bottom-right':
      ctx.translate(-epsilon_x, -epsilon_y);
      ctx.beginPath();
      ctx.rect(width - borderwrap, height - borderwrap, borderwrap + 2 * epsilon_x, borderwrap, + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(2 * (width - borderwrap), 2 * (height - borderwrap));
      ctx.scale(-1, -1);
      break;
    case 'bottom':
      ctx.translate(0, -epsilon_y);
      ctx.beginPath();
      ctx.rect(borderwrap, height - borderwrap, width - 2 * borderwrap + 2 * epsilon_x, borderwrap + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(0, 2 * (height - borderwrap));
      ctx.scale(1, -1);
      break;
    case 'bottom-left':
      ctx.translate(epsilon_x, -epsilon_y);
      ctx.beginPath();
      ctx.rect(0, height - borderwrap, borderwrap + 2 * epsilon_x, borderwrap + 2 * epsilon_y);
      ctx.clip();
      ctx.translate(2 * borderwrap, 2 * (height - borderwrap));
      ctx.scale(-1, -1);
      break;
    case 'left':
      ctx.translate(epsilon_x, 0);
      ctx.beginPath();
      ctx.rect(0, borderwrap, borderwrap, height - 2 * borderwrap);
      ctx.clip();
      ctx.translate(2 * borderwrap, 0);
      ctx.scale(-1, 1);
      break;
    }
  }

  renderText(element) {
  }

  renderScoreline(element) {
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);

    this.ctx.globalAlpha = 0.5;
    this.ctx.fillStyle = '#aaaaaa7f';

    this.ctx.fillRect(0, 0, width, height);
  }

  renderGrid(element) {
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);

    const sidebar = this.parseFloatAttr(element, 'sidebar');
    const header = this.parseFloatAttr(element, 'header');
    const stroke = this.parseFloatAttr(element, 'stroke', 1);
    const color = this.parseColorAttr(element, 'color');

    const sidebar_width = sidebar * width;
    const header_height = height * header;
    const sidebar_height = Math.max(0, height - header_height);
    const header_width = Math.max(0, width - sidebar_width);

    const sidebar_color = this.parseColorAttr(element, 'sidebarcolor');
    const header_color = this.parseColorAttr(element, 'headercolor');

    this.ctx.fillStyle = header_color;
    this.ctx.fillRect(sidebar_width, 0, header_width, header_height);

    this.ctx.fillStyle = sidebar_color;
    this.ctx.fillRect(0, header_height, sidebar_width, sidebar_height);

    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = stroke;

    for (let i = 0; i < 7; i++) {
      const x = sidebar_width + (width - sidebar_width)/7 * i;
      this.ctx.beginPath();
      this.ctx.moveTo(x, i === 0 ? 0 : header_height);
      this.ctx.lineTo(x, height);
      this.ctx.stroke();
    }

    for (let i = 0; i < 5; i++) {
      const y = header_height + (height - header_height)/5 * i;
      this.ctx.beginPath();
      this.ctx.moveTo(i === 0 ? 0 : sidebar_width, y);
      this.ctx.lineTo(width, y);
      this.ctx.stroke();
    }

    this.ctx.strokeRect(0, 0, width, height);
  }

  renderBarcode(element) {
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);

    // This is currently hardcoded. We probably don't need a real barcode render for canvas (even though it's fun).
    const barcode_number = '100201009013';
    const ean13_encoded = '10100011010001101001101100011010110011010011101010111001011101001110010110011010000101100110101';

    // 115 and 120 are the default width/height for the ruby Barby renderer. We try to match it's output here.
    const xscale = width / 115;
    const yscale = height / 120;

    const xmargin = 10 * xscale;
    const ymargin = 10 * yscale;
    const available_width = width - 2 * xmargin;
    const bar_height = height - 2 * ymargin;
    const xdim = available_width / ean13_encoded.length;

    let x = xmargin;
    const y = ymargin;
    let bar_width = 0;

    this.ctx.fillStyle = '#fff';

    this.ctx.fillRect(0, 0, width, height);

    this.ctx.fillStyle = '#000';

    const maxn = ean13_encoded.length;
    for (let n = 0; n <= maxn; n++) {
      if (ean13_encoded[n] === '1') {
        bar_width += xdim;
      } else if (bar_width > 0) {
        this.ctx.fillRect(x - bar_width, y, bar_width, bar_height);
        bar_width = 0;
      }
      x += xdim;
    }
  }

  renderQrCode(element) {
    const data = element.textContent || '';
    const size = this.parseFloatAttr(element, 'size');
    const ec_level = element.getAttribute('ec') || 'M';
    const color = this.parseColorAttr(element, 'color');

    const qr = new JSQRCode(0, ec_level.toUpperCase());
    qr.addData(data, 'Byte');
    qr.make();

    const path = new Path2D(qr.toSVGPath());
    const scale = size / qr.getModuleCount();

    this.saveGraphicsState(() => {
      this.ctx.scale(scale, scale);
      this.ctx.fillStyle = color;
      this.ctx.fill(path);
    });
  }

  renderPdf(element) {
    const x = this.parseFloatAttr(element, 'x');
    const y = this.parseFloatAttr(element, 'y');
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);

    const pdf = this.getPdfFromElement(element);

    let src;

    if (pdf) {
      const max_size = Math.max(width, height) * this.render_frame.scale;
      src = this.pdfUrl(pdf, max_size, element);
    } else if (element.hasAttribute('src') && element.getAttribute('src').startsWith('del:')) {
      src = CanvasPageRenderer.DELETED_IMAGE_SRC;
    } else {
      src = CanvasPageRenderer.MISSING_IMAGE_SRC;
    }

    const img = this.fetchImage(src);
    if (img && img.success) {
      this.ctx.drawImage(img.data, 0, 0, width, height);
    }

    // TODO: Handle img.success == false
  }

  applyElementTransform(element, fn) {
    const x = this.parseFloatAttr(element, 'x');
    const y = this.parseFloatAttr(element, 'y');
    const width = this.getElementWidth(element);
    const height = this.getElementHeight(element);
    const rotation = this.parseFloatAttr(element, 'rotate');
    const rad = Math.PI * rotation / 180;

    const opacity = this.parseFloatAttr(element, 'opacity', 1);

    this.saveGraphicsState(() => {
      this.ctx.translate(x, y);
      this.ctx.translate(width/2, height/2);
      this.ctx.rotate(rad);
      this.ctx.translate(-width/2, -height/2);
      this.ctx.globalAlpha = opacity;

      fn.call();
    });
  }

  isRenderingBaseLayer() {
    return !this.layers || this.layers.includes(null);
  }

  shouldRenderPdfLayer(name) {
    if (!this.layers) {
      return true;
    }
    return this.layers.includes(name || null);
  }

  isPdfLayerVisible(name) {
    if (!name) {
      return true;
    }

    const layer = this.layer_definitions && this.layer_definitions.find(l => l.name === name);
    if (!layer) {
      return true;
    }

    if (layer.visibility === 'on' || (layer.visibility === 'fulfillment' && this.fulfillment)) {
      return true;
    }

    return false;
  }

  saveGraphicsState(fn) {
    this.ctx.save();
    try {
      fn.call();
    } finally {
      this.ctx.restore();
    }
  }

  buildRenderFrame(print_page, opts) {
    // TODO: how do we get the layout hash???
    //book_layout_hash = print_page.print_book.try(:layout_hash)
    const book_layout_hash = null;
    const page_node = Px.Util.parseXML(print_page.data).firstChild;
    const page_width = this.parseFloatAttr(page_node, 'width');
    const page_height = this.parseFloatAttr(page_node, 'height');

    const view_box = this.getViewBox(page_node, book_layout_hash, opts);

    // We limit size of previews to max 2400 px, but enforce no limit in fulfillment mode,
    // or when rendering inline pages (because these can be cropped/zoomed and may have to
    // be rendered at more than 2400px even though the final output is <= 2400px).
    const max_size = (this.fulfillment || this.render_stack.length > 0) ? null : 2400;
    // We increase the max size if cropping to an element (that's a feature we use for generating HappyAR presets)
    // on the fly, and unfortunately we currently have to generate pretty large images for that use case.
    if (opts.crop_element) {
      max_size = CanvasPageRenderer.HAPPYAR_PRESET_SIZE;
    }
    const scale = this.calculateScale(page_width, page_height, view_box, max_size, opts);
    const preserve_aspect_ratio = opts.preserve_aspect_ratio === false ? false : true;

    let cropped_pixel_width, cropped_pixel_height;

    if (!preserve_aspect_ratio && opts.width && opts.height) {
      cropped_pixel_width = Math.round(opts.width);
      cropped_pixel_height = Math.round(opts.height);
    } else {
      cropped_pixel_width = Math.round(view_box[2] * scale);
      cropped_pixel_height = Math.round(view_box[3] * scale);
    }

    const page_pixel_width = Math.round(page_width * scale);
    const page_pixel_height = Math.round(page_height * scale);

    return {
      print_page: print_page,
      page_node: page_node,
      preserve_aspect_ratio: preserve_aspect_ratio,
      view_box: view_box,
      scale: scale,
      page_width: page_width,
      page_height: page_height,
      page_pixel_width: page_pixel_width,
      page_pixel_height: page_pixel_height,
      cropped_pixel_width: cropped_pixel_width,
      cropped_pixel_height: cropped_pixel_height,
      // When fulfilling, this should match the value specified in the template definition.
      dpi: Math.round((page_pixel_width / page_width) * 25.4),
      watermark: opts.watermark === true,
      book_layout_hash: book_layout_hash
    };
  }

  getViewBox(page_node, book_layout_hash, opts) {
    const page_width = this.parseFloatAttr(page_node, 'width');
    const page_height = this.parseFloatAttr(page_node, 'height');

    let x, y, width, height;

    if (opts.crop_element) {
      // NOTE: This currently doesn't work if target element is inside an ipage.
      const element = page_node.querySelector(`[name="${opts.crop_element}"]`);
      if (!element) {
        throw new Error(`Failed rendering page; element ${opts.crop_element} not found.`);
      }
      x = this.parseFloatAttr(element, 'x');
      y = this.parseFloatAttr(element, 'y');
      width = this.parseFloatAttr(element, 'width');
      height = this.parseFloatAttr(element, 'height');
    } else if (opts.relative_crop) {
      const [cx, cy, cw, ch] = opts.relative_crop;
      x = cx * page_width;
      y = cy * page_height;
      width = cw * page_width;
      height = ch * page_height;
    } else if (this.fulfillment && page_node.getAttribute('crop')) {
      const crop = page_node.getAttribute('crop').split(/\s+/).map(c => parseFloat(c));
      let carr;
      switch (crop.length) {
      case 1:
        carr = [crop[0], crop[0], crop[0], crop[0]];
        break;
      case 2:
        carr = [crop[0], crop[1], crop[0], crop[1]];
        break;
      case 3:
        carr = [crop[0], crop[1], crop[2], crop[1]];
        break;
      default:
        carr = crop;
      }
      x = carr[3];
      y = carr[0];
      width = page_width - carr[3] - carr[1];
      height = page_height - carr[2] - carr[0];
    } else if (this.fulfillment) {
      x = 0;
      y = 0;
      width = page_width;
      height = page_height;
    } else {
      let bleed_t, bleed_r, bleed_b, bleed_l;
      const gutter = this.parseFloatAttr(page_node, 'gutter');
      const has_gutter = gutter > 0;
      if (opts.crop) {
        [bleed_t, bleed_r, bleed_b, bleed_l] = Px.Util.parseCSSRectValues(page_node.getAttribute('bleed'));
      } else {
        bleed_t = bleed_r = bleed_b = bleed_l = 0;
      }
      if (has_gutter) {
        // TODO: Fix this.
        if (this.pagePosition(book_layout_hash, null) === 'right') {
          x = gutter;
          width = page_width - (gutter + bleed_r);
        } else {
          x = bleed_l;
          width = page_width - (gutter + bleed_l);
        }
      } else {
        x = bleed_l;
        width = page_width - (bleed_l + bleed_r);
      }
      y = bleed_t;
      height = page_height - (bleed_t + bleed_b);
    }

    return [x, y, width, height];
  }

  pagePosition(layout_hash, print_page) {
    // TODO: Include page position in api page hash instead?
    // TODO: Implement!
    return 'left';
  }

  // TODO: Implement fully!
  calculateScale(page_width, page_height, view_box, max_preview_size, opts) {
    let wscale = 1;
    let hscale = 1;

    if (opts.dpi) {
      if (opts.width || opts.height) {
        throw new Error('dpi option cannot be used together with width/height');
      }
      let width = (opts.dpi / 25.4) * page_width;
      if (max_preview_size && width > max_preview_size) {
        width = max_preview_size;
      }
      wscale = width / page_width;
      let height = (opts.dpi / 25.4) * page_height;
      if (max_preview_size && height > max_preview_size) {
        height = max_preview_size;
      }
      hscale = height / page_height;
    } else {
      if (opts.width) {
        let width = opts.width;
        if (max_preview_size && width > max_preview_size) {
          width = max_preview_size;
        }
        wscale = width / view_box[2];
      }
      if (opts.height) {
        let height = opts.height;
        if (max_preview_size && height > max_preview_size) {
          height = max_preview_size;
        }
        hscale = height / view_box[3];
      }
    }

    return Math.min(wscale, hscale);
  }

  getOptimalSize(size) {
    size = size || 0;
    size *= this.render_frame.scale;
    const standard_sizes = CanvasPageRenderer.STANDARD_SIZES;
    let optimal = standard_sizes[0];
    let i = 1;
    while (optimal < size && i < standard_sizes.length) {
      optimal = standard_sizes[i];
      i++;
    }
    return optimal;
  }

  getElementWidth(element) {
    const element_name = element.tagName.toLowerCase();
    let width = this.parseFloatAttr(element, element_name === 'qrcode' ? 'size' : 'width');
    if (element_name === 'image') {
      // Allow elements to be max 4x as wide as page diagonal when using 2-page-spread, otherwise 2x.
      const diagonal = Math.sqrt(this.render_frame.page_pixel_width**2 + this.render_frame.page_pixel_height**2);
      const max_width = diagonal * (element.getAttribute('clone_id') ? 4 : 2);
      const scale = this.render_frame.scale;
      if (width * scale > max_width) {
        width = max_width/scale;
      }
    }
    return width;
  }

  getElementHeight(element) {
    const element_name = element.tagName.toLowerCase();
    let height = this.parseFloatAttr(element, element_name === 'qrcode' ? 'size' : 'height');
    if (element_name === 'image') {
      // Allow elements to be max 4x as wide as page diagonal when using 2-page-spread, otherwise 2x.
      const diagonal = Math.sqrt(this.render_frame.page_pixel_width**2 + this.render_frame.page_pixel_height**2);
      const max_height = diagonal * (element.getAttribute('clone_id') ? 4 : 2);
      const scale = this.render_frame.scale;
      if (height * scale > max_height) {
        height = max_height/scale;
      }
    }
    return height;
  }

  getNodeChildren(node) {
    const graphic_elements = [];
    const score_elements = [];
    const barcode_elements = [];
    for (let element of node.children) {
      switch (element.tagName.toLowerCase()) {
      case 'image':
      case 'text':
      case 'pdf':
      case 'g':
      case 'grid':
      case 'ipage':
      case 'qrcode':
        if (!this.removeBecausePlaceholder(element)) {
          graphic_elements.push(element);
        }
        break;
      case 'score':
        score_elements.push(element);
        break;
      case 'barcode':
        barcode_elements.push(element);
        break;
      }
    };
    // Markup z to illustrate original order of graphic elements in XML.
    const elements_by_z = {};
    graphic_elements.forEach(element => {
      let z = 0;
      if (element.tagName.toLowerCase() === 'text') {
        z = 33;
      } else if (element.getAttribute('z')) {
        z = parseInt(element.getAttribute('z'), 10);
      }
      if (!elements_by_z.hasOwnProperty(z)) {
        elements_by_z[z] = [];
      }
      elements_by_z[z].push(element);
    });
    let sorted_graphic_elements = [];
    var sorted_keys = _.sortBy(Object.keys(elements_by_z), function(key) {
      return parseInt(key, 10);
    });
    sorted_keys.forEach(function(z) {
      sorted_graphic_elements = sorted_graphic_elements.concat(elements_by_z[z]);
    });
    var elements_in_z_order = [].concat(
      sorted_graphic_elements,
      score_elements,
      barcode_elements
    );
    return elements_in_z_order;
  }

  imageUrl(image, size) {
    const optimal_size = this.getOptimalSize(size);
    let image_url;
    image.thumbnails.forEach(thumb => {
      if (thumb.size === optimal_size) {
        image_url = thumb.url;
      }
    });

    if (!image_url) {
      console.error(`Could not find Image thumbnail URL: ${image.id} (size: ${optimal_size})`);
      return null;
    }

    return image_url;
  }

  imageUrlWithEffects(image, size, element) {
    const params = this.imageEffectParams(element);
    const base_url = this.imageUrl(image, size);

    let url;

    const parts = base_url.split('/~/');
    if (params.brightness) {
      parts[0] += `/-/brightness/${params.brightness + 100}`;
    }
    if (params.saturation) {
      parts[0] += `/-/saturate/${params.saturation + 100}`;
    }
    if (params.contrast) {
      parts[0] += `/-/contrast/${params.contrast + 100}`;
    }
    if (params.grayscale) {
      parts[0] += '/-/grayscale';
    }
    if (params.sepia) {
      parts[0] += '/-/sepia';
    }
    if (params.color) {
      parts[0] += `/-/color/${params.color.replace('#', '')}`;
    }
    if (params.flip) {
      parts[0] += '/-/flip';
    }
    if (params.flipv) {
      parts[0] += '/-/flip/vertical';
    }
    url = parts.join('/~/');

    return url;
  }

  pdfUrl(pdf, size, element) {
    const optimal_size = this.getOptimalSize(size);
    let pdf_url;
    pdf.thumbnails.forEach(thumb => {
      if (thumb.size === optimal_size) {
        pdf_url = thumb.url;
      }
    });

    if (!pdf_url) {
      console.error(`Could not find PDF thumbnail URL: ${pdf.id} (size: ${optimal_size})`);
      return null;
    }

    const page = element.getAttribute('page');
    if (page) {
      const parts = pdf_url.split('/~/');
      parts[0] = parts[0] + `/-/page/${page}`;
      pdf_url = parts.join('/~/');
    }

    return pdf_url;
  }

  imageEffectParams(element) {
    const params = {};

    const brightness = this.parseIntegerAttr(element, 'brightness');
    if (brightness !== 0) {
      params.brightness = brightness;
    }

    const saturation = this.parseIntegerAttr(element, 'saturation');
    if (saturation !== 0) {
      params.saturation = saturation;
    }

    const contrast = this.parseIntegerAttr(element, 'contrast');
    if (contrast !== 0) {
      params.contrast = contrast;
    }

    if (element.getAttribute('sepia') === 'true') {
      params.sepia = true;
    }

    if (element.getAttribute('grayscale') === 'true') {
      params.grayscale = true;
    }

    const color = this.parseColorAttr(element, 'color');
    if (color !== '#000000') {
      params.color = color.replace('#', '');
    }

    if (element.getAttribute('flip') === 'true') {
      params.flip = true;
    }

    if (element.getAttribute('flipv') === 'true') {
      params.flipv = true;
    }

    return params;
  }

  getImageFromElement(element) {
    return this.getImageFromSrc(element.getAttribute('src'));
  }

  getImageFromSrc(src) {
    let image;

    const src_match = src.match(/(^[\w\.]*):(.*)/);
    if (src_match && src_match[1] === 'db') {
      const image_id = src_match[2];
      image = this.images[image_id];
    }

    if (!image) {
      console.error(`Image info not found: ${src} while rendering page ${this.render_frame.print_page.id}`);
    }

    return image;
  }

  getPdfFromElement(element) {
    return this.getPdfFromSrc(element.getAttribute('src'));
  }

  getPdfFromSrc(src) {
    let pdf;

    const src_match = src.match(/(^[\w\.]*):(.*)/);
    if (src_match && src_match[1] === 'db') {
      const pdf_id = src_match[2];
      pdf = this.pdfs[pdf_id];
    }

    if (!pdf) {
      console.error(`Pdf info not found: ${src} while rendering page ${this.render_frame.print_page.id}`);
    }

    return pdf;
  }

  fetchImage(image_url) {
    return this.fetchAsyncResource(image_url, blob => {
      return createImageBitmap(blob);
    });
  }

  fetchAsyncResource(url, blobHandler) {
    const resource = this.async_resources[url];

    if (resource && resource.loaded) {
      return resource;
    }

    this.are_resources_pending = true;

    if (!resource) {
      this.loadUrl(url, blobHandler);
    }

    return null;
  }

  async loadUrl(url, blobHandler) {
    const resources = this.async_resources;
    resources[url] = {loaded: false, success: null, data: null};

    let response;
    try {
      response = await fetch(url);
    } catch (err) {
      console.error(`Fetch request ${url} failed: ${err}`);
      resources[url].loaded = true;
      resources[url].success = false;
      this.rerender();
      return;
    }

    if (!response.ok) {
      console.error(`HTTP error stauts ${url}: ${response.status}`);
      resources[url].loaded = true;
      resources[url].success = false;
      this.rerender();
      return;
    }

    let buffer;
    try {
      buffer = await response.arrayBuffer();
    } catch (err) {
      console.error(`Failed loading images buffer ${url}: ${err}`);
      resources[url].loaded = true;
      resources[url].success = false;
      this.rerender();
      return;
    }

    const blob = new Blob([buffer], {type: response.headers.get('Content-Type')});

    let data;
    try {
      data = await blobHandler(blob);
    } catch (err) {
      console.error(`Blob handler failed ${urj}: ${err}`);
      resources[url].loaded = true;
      resources[url].success = false;
      this.rerender();
      return;
    }

    resources[url].loaded = true;
    resources[url].success = true;
    resources[url].data = data;

    this.rerender();
  }

  positionedImageDimensions(width, height, element, img_ratio) {
    const zoom_factor = 1 + this.parseFloatAttr(element, 'zoom') / 100.0;
    const crotation = this.parseFloatAttr(element, 'crotation');

    let w, h;

    if (element.getAttribute('crop') === 'false') {
      let inscribed_image_ar = img_ratio;
      if (element.getAttribute('stretch') === 'true') {
        const circumscribed_dims = Px.Util.circumscribedRectangleDimensions(width, height, crotation);
        inscribed_image_ar = circumscribed_dims.width / circumscribed_dims.height;
      }
      const dimensions = Px.Util.inscribedRectangleDimensions(width, height, crotation, inscribed_image_ar);
      w = dimensions.width * zoom_factor;
      h = dimensions.height * zoom_factor;
    } else {
      const circumscribed_dimensions = Px.Util.circumscribedRectangleDimensions(width, height, crotation);
      w = circumscribed_dimensions.width * zoom_factor;
      h = circumscribed_dimensions.height * zoom_factor;

      if (element.getAttribute('stretch') !== 'true') {
        if (img_ratio >= w/h) {
          w = h * img_ratio;
        } else {
          h = w / img_ratio;
        }
      }
    }

    return [w, h];
  }

  resolutionTooLow(px_width, px_height, element) {
    const borderwrap = this.parseFloatAttr(element, 'borderwrap');
    const width = this.getElementWidth(element) - 2 * borderwrap;
    const height = this.getElementHeight(element) - 2 * borderwrap;
    const crotation = this.parseFloatAttr(element, 'crotation');
    const zoom = this.parseFloatAttr(element, 'zoom');

    const img_ratio = px_width / px_height;
    const rendered_dimensions = this.positionedImageDimensions(width, height, element, img_ratio);

    px_width = px_width - (px_width*(zoom/100.0));
    px_height = px_height - (px_height*(zoom/100.0));
    const min_per_mm = this.minimum_dpi / 25.4;
    return (px_width/rendered_dimensions[0] < min_per_mm) || (px_height/rendered_dimensions[1] < min_per_mm);
  }

  removeBecausePlaceholder(element) {
    if (!(this.hide_placeholders || this.fulfillment)) {
      return false;
    }

    let is_placeholder = element.getAttribute('placeholder') === 'true';
    const element_type = element.tagName.toLowerCase();
    if (element_type === 'image') {
      is_placeholder = is_placeholder || !element.hasAttribute('src');
    } else if (element_type === 'text') {
      if (this.disable_text_removal) {
        is_placeholder = false;
      }
    }

    // Remove all placeholders in fulfillment. When rendering previews,
    // keep those that have the show_on_preview flag enabled.
    if (this.fulfillment) {
      return is_placeholder;
    } else {
      return is_placeholder && element.getAttribute('show_on_preview') !== 'true';
    }
  }

  parseIntegerAttr(node, attribute, fallback=0) {
    if (node.hasAttribute(attribute)) {
      const result = parseInt(node.getAttribute(attribute), 10);
      if (Number.isNaN(result)) {
        return fallback;
      }
      return result;
    }
    return fallback;
  }

  parseFloatAttr(node, attribute, fallback=0) {
    if (node.hasAttribute(attribute)) {
      const result = parseFloat(node.getAttribute(attribute));
      if (Number.isNaN(result)) {
        return fallback;
      }
      return result;
    }
    return fallback;
  }

  parseColorAttr(node, attribute, fallback='#000000') {
    if (node.hasAttribute(attribute)) {
      const result = Px.Util.colorForDisplay(node.getAttribute(attribute));
      return result || fallback;
    }
    return fallback;
  }

  getBufferCanvasContext(width, height) {
    if (!this.buffer_canvas) {
      this.buffer_canvas = document.createElement('canvas');
      this.buffer_ctx = this.buffer_canvas.getContext('2d');
    } else {
      this.buffer_ctx.reset();
    }
    const pr = window.devicePixelRatio || 1;
    this.buffer_canvas.width = Math.floor(width * pr);
    this.buffer_canvas.height = Math.floor(height * pr);
    this.buffer_ctx.reset();
    this.buffer_ctx.scale(pr, pr);
    return this.buffer_ctx;
  }

  releaseCanvasResources() {
    this.ctx.reset();
    this.canvas.width = 1;
    this.canvas.height = 1;

    if (this.buffer_ctx) {
      this.buffer_ctx.reset();
      this.buffer_canvas.width = 1;
      this.buffer_canvas.height = 1;
    }
    // TODO: Close image bitmaps, etc.
  }

  emitProgress() {
    if (!this.onprogress) {
      return;
    }

    const seq_num = this.render_sequence_number;

    this.canvas.toBlob(blob => {
      // We only render if no additional renders have happed since.
      if (seq_num === this.render_sequence_number) {
        this.onprogress.call(null, blob);
      }
    }, 'image/webp');
  }

  emitComplete() {
    if (!this.oncomplete) {
      this.releaseCanvasResources();
      return;
    }

    this.canvas.toBlob(blob => {
      this.oncomplete.call(null, blob);
      this.releaseCanvasResources();
    }, 'image/webp');
  }

};

// Keep this in sync with Image.STANDARD_THUMBNAIL_SIZES.
Px.CanvasPageRenderer.STANDARD_SIZES = [150, 250, 600, 900, 1200];
Px.CanvasPageRenderer.HAPPYAR_PRESET_SIZE = 1500;

Px.CanvasPageRenderer.MISSING_IMAGE_SRC = '/images/missing-image.png?t=20250627';
Px.CanvasPageRenderer.DELETED_IMAGE_SRC = '/images/deleted-image.png?t=20250328';

Px.CanvasPageRenderer.PLACEHOLDER_10x10_TRANSPARENT = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAQAAAAnOwc2AAAAD0lEQVR42mNkwAIYh7IgAAVVAAuInjI5AAAAAElFTkSuQmCC';
Px.CanvasPageRenderer.PLACEHOLDER_10x10_WHITE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAQAAAAnOwc2AAAAEUlEQVR42mP8/58BAzAOZUEA5OUT9xiCXfgAAAAASUVORK5CYII=';
Px.CanvasPageRenderer.PLACEHOLDER_10x10_GRAY = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAGUlEQVQYV2PcvXv3fwYiAOOoQnyhRP3gAQDMIh/rateWoAAAAABJRU5ErkJggg==';
